import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { TableStore } from 'shared/ui/TableNew'
import { showToast } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { RequestLoadData } from 'shared/api/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { tableLimitStore } from 'entities/TableLimit'
import { type IParamsTagsPerPage, type IResponseTagsPerPage, TagsApi } from 'entities/Tags'
import { TagsRow } from 'entities/Tags/model/TagsRow'
import { TagsModalStore } from 'pages/settings/pages/tags/store/TagsModalStore'
import { SettingsRoutes } from 'pages/settings/route/type'

export class TagsStore {
  private readonly _tableStore: TableStore<TagsRow>
  private _tagsModalStore: TagsModalStore | null = null
  private _initialLoading = true
  private _loading = false
  private _listMap = new Map<number, TagsRow>()
  private _search = ''
  private _page = 1
  private _total = 0
  private _limit = tableLimitStore.getLimit('settings-tags-table') ?? 10
  private _disposeReaction: IReactionDisposer | null = null
  private _idsToDelete: number[] = []

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
    this._tableStore = new TableStore({
      key: 'settings_tags',
      element: 'number',
      page: this._page,
      limit: 10,
      getLimit: () => tableLimitStore.getLimit('settings-tags-table'),
      setLimit: (value) => tableLimitStore.setLimit('settings-tags-table', value),
    })

    this._disposeReaction = reaction(
      () => this._listRequestParams,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  init = async () => {
    await Promise.all([
      this._entities.userPermissionsStore.initPromise,
      this._entities.usersStore.initPromise,
    ])

    await this.loadData()

    runInAction(() => {
      this._tagsModalStore = new TagsModalStore(
        this.isUserCanManageTags,
        this.loadData,
        this.resetData
      )
      this._initialLoading = false
    })

    return this.dispose
  }

  dispose = () => {
    this._disposeReaction?.()
    this._requestLoadData.dispose()
    this._tagsModalStore?.dispose()
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      runInAction(() => {
        this._tableStore.setLoading(true)
        this._loading = true
      })

      const { data } = await TagsApi.getTagsPerPage(this._listRequestParams, {
        cancelToken: cancelRequest.token,
      })

      this.setData(data)
    },
    errorCallback: () => {
      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again.',
      })
    },
    finallyCallback: () => {
      runInAction(() => {
        this._tableStore.setLoading(false)
        this._loading = false
      })
    },
  })

  loadData = async () => {
    return this._requestLoadData.loadData()
  }

  setData = ({ data, total, current_page }: IResponseTagsPerPage) => {
    this._listMap.clear()
    const rows = data.map((item) => new TagsRow(item))

    rows.forEach((row) => {
      this._listMap.set(row.id, row)
    })

    this._tableStore.setRows(rows)
    this._tableStore.setTotal(total)
    this._tableStore.setPage(current_page)
    this._page = current_page
    this._total = total
  }

  resetData = () => {
    this._search = ''
    this._page = this.calculatePageAfterChanges()
    this._tableStore.resetSelected()
    this._total = 0
    this.setIdsToDelete([])
  }

  private calculatePageAfterChanges() {
    const deletedCount = this._idsToDelete.length
    const newTotal = this._total - deletedCount
    const newLastPage = newTotal > 0 ? Math.ceil(newTotal / this._limit) : 1

    if (this._page > newLastPage) {
      return newLastPage
    }

    return this._page
  }

  onShowContacts = (id: number) => {
    uiStore.changeRouteViaVue({
      path: `/${SettingsRoutes.root}/${SettingsRoutes.tags}/${id}`,
    })
  }

  onBulkDelete = () => {
    this.setIdsToDelete(this._tableStore.selectedIds)
    this._tagsModalStore?.onDeleteBulk(this._tableStore.selectedIds)
  }

  changePaging = (page: number, limit: number) => {
    this._page = page
    this._limit = limit
    this._loading = true
  }

  setSearch = (value: string) => {
    this._page = 1
    this._search = value
    this._loading = true
  }

  setIdsToDelete = (ids: number[]) => {
    this._idsToDelete = ids
  }

  private get _listRequestParams(): IParamsTagsPerPage {
    const params: IParamsTagsPerPage = {
      page: this._page,
      length: this._limit,
      sortBy: this._tableStore.sortBy || 'created_at',
      sortOrder: this._tableStore.sortOrder,
    }

    if (this._search) params.search = this._search

    return params
  }

  get initialLoading() {
    return this._initialLoading
  }

  get loading() {
    return this._loading
  }

  get search() {
    return this._search
  }

  get tableStore() {
    return this._tableStore
  }

  get tagsModalStore() {
    return this._tagsModalStore
  }

  get items() {
    return Array.from(this._listMap.values())
  }

  get isEmpty() {
    return !this._initialLoading && !this._loading && !this._listMap.size && !this._search.length
  }

  get isUserCanManageTags() {
    return (
      this._entities.userPermissionsStore.getItem('can_manage_tags') &&
      !this._entities.usersStore.user?.isViewOnlyRole
    )
  }

  get isCheckboxDisabled() {
    return this._tagsModalStore?.isLoadingDeleteBulkModal ?? false
  }
}
